// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import React, { RefObject, useEffect, useState } from "react";

import { mdiPlus } from "@mdi/js";
import { Alert, Button, Input, Modal } from "antd";
import { AxiosError } from "axios";
import styled from "styled-components";

import { GeoMapSettings } from "../../models/settings";
import { LabelWrapper } from "./LabelWrapper";

export interface GeoMapSettingsProps {
  settings: GeoMapSettings;
  showSettings: boolean;
  containerRef: RefObject<HTMLDivElement>;
  onSave: (settings: GeoMapSettings) => Promise<void>;
  onCancel: () => void;
}

export function SettingsModal({
  settings,
  containerRef,
  showSettings,
  onCancel,
  onSave
}: GeoMapSettingsProps) {
  const [error, setError] = useState<AxiosError>(undefined);
  const [customFields, setCustomFields] = useState(settings?.customFields ?? []);

  useEffect(() => {
    setCustomFields(settings?.customFields ?? []);
  }, [settings]);

  if (!settings) {
    return null;
  }

  return (
    <Modal
      open={showSettings}
      onCancel={onCancel}
      className="geoSettingsModal"
      style={{ top: "15%" }}
      width={780}
      title={"Manage Available Custom Fields"}
      onOk={async () => {
        setError(undefined);
        try {
          await onSave(
            new GeoMapSettings(settings.id, customFields, settings.systemFields)
          );
        } catch (err) {
          setError(err);
        }
      }}
      getContainer={containerRef?.current ? () => containerRef.current : false}>
      <RootContainer>
        <Table>
          {customFields.map((field, i) => {
            return (
              <Row key={i}>
                <Col width={160}>
                  <LabelWrapper label="Field Name">
                    <Input
                      type="text"
                      value={field.name}
                      onPressEnter={() => {
                        setCustomFields([
                          ...customFields,
                          { name: "", unit: null, min: null, max: null, notes: "" }
                        ]);
                      }}
                      onChange={(e) => {
                        const newFields = customFields.map((f, idx) =>
                          idx === i ? { ...f, name: e.target.value } : f
                        );
                        setCustomFields(newFields);
                      }}
                    />
                  </LabelWrapper>
                </Col>
                <Col>
                  <LabelWrapper label="Unit">
                    <Input
                      onChange={(e) => {
                        const newFields = customFields.map((f, idx) =>
                          idx === i ? { ...f, unit: e.target.value } : f
                        );
                        setCustomFields(newFields);
                      }}
                      placeholder={"Unit"}
                      value={field.unit}
                    />
                  </LabelWrapper>
                </Col>
                <Col>
                  <LabelWrapper label="Min">
                    <Input
                      type={"number"}
                      data-testid={"geo-map-min-input"}
                      value={field.min}
                      placeholder={"Min"}
                      onChange={(e) => {
                        const newFields = customFields.map((f, idx) =>
                          idx === i ? { ...f, min: e.target.valueAsNumber } : f
                        );
                        setCustomFields(newFields);
                      }}
                    />
                  </LabelWrapper>
                </Col>
                <Col>
                  <LabelWrapper label="Max">
                    <Input
                      type={"number"}
                      data-testid={"geo-map-min-input"}
                      value={field.max}
                      placeholder={"Max"}
                      onChange={(e) => {
                        const newFields = customFields.map((f, idx) =>
                          idx === i ? { ...f, max: e.target.valueAsNumber } : f
                        );
                        setCustomFields(newFields);
                      }}
                    />
                  </LabelWrapper>
                </Col>
                <Col width={200}>
                  <LabelWrapper label="Notes">
                    <Input
                      type={"text"}
                      data-testid={"geo-map-min-input"}
                      value={field.notes}
                      placeholder={"Notes"}
                      onChange={(e) => {
                        const newFields = customFields.map((f, idx) =>
                          idx === i ? { ...f, notes: e.target.value } : f
                        );
                        setCustomFields(newFields);
                      }}
                    />
                  </LabelWrapper>
                </Col>
              </Row>
            );
          })}
        </Table>
        <ButtonWrapper>
          <Button
            icon={<Icon path={mdiPlus} size={1.0} />}
            type="primary"
            onClick={() => {
              setCustomFields([
                ...customFields,
                { name: "", unit: null, min: null, max: null, notes: "" }
              ]);
            }}>
            Add New Field
          </Button>
        </ButtonWrapper>
        {error && (
          <Alert
            type={"error"}
            message={error.response?.data ?? error?.message}
            showIcon
          />
        )}
      </RootContainer>
    </Modal>
  );
}
const Table = styled.div`
  display: table;
  width: 100%;
  height: 100%;
`;
const RootContainer = styled.div`
  padding: 1rem;
`;
const ButtonWrapper = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  padding: 1rem;

  .ant-btn {
    display: flex;
    align-items: center;
    gap: 5px;
  }
`;

const Row = styled.div`
  display: table-row;
  justify-content: space-between;
  padding: 10px;
`;
const Col = styled.div`
  display: table-cell;
  padding: 2px;
  width: ${(props) => (props.width ? props.width + "px" : "100px")};
`;
